type WebEnvironment = "test" | "production";

interface EnvironmentConfig {
  shopify: {
    storeDomain: string;
    /**
     * The store whose theme prints Shop Pay Installments' plan data, read at
     * build time for the installments line (shopPayInstallments). Local runs
     * borrow the live store's: the test store has no Shop Pay Installments,
     * so the line would never show there. Prices, cart and checkout stay on
     * `storeDomain`.
     */
    installmentsStoreDomain: string;
    storefrontToken: string;
    products: {
      matic: string;
      maticVariant: string;
      care: string;
      careVariant: string;
      bagPass: string;
      bagPassVariant: string;
      referralBagPass: string;
      referralBagPassVariant: string;
      refkitVariant: string;
      builderKit: string;
      builderKitVariant: string;
      stickerKit: string;
      stickerKitVariant: string;
      raceCarStickers: string;
      raceCarStickersVariant: string;
      petBrushRoll: string;
      petBrushRollVariant: string;
      dock: string;
      dockVariant: string;
      /**
       * Matic Serene: a Matic in its own right (not a trim on the base Matic),
       * kept so past Serene orders still read as Matic orders.
       */
      maticSerene: string;
      maticSereneVariant: string;
      bundleProductIds: string[];
      accessoryProductIds: string[];
    };
  };
  web: {
    domain: string;
    baseUrl: string;
    cdnResizerUrl: string;
    upgradeInsecureRequests: boolean;
  };
}

// Shared across both stores (identical in test and production).
const SHARED = {
  // Videos and large downloads live in the matic-media R2 bucket; serving
  // them from the app host is what ran up the Amplify data-transfer bill.
  media: {
    baseUrl: "https://media.maticrobots.com",
  },
  posthog: {
    key: "phc_y8wb4BLxuuNynHMrdtAT8Ws5LbRaVqkTvLDvxw3dq7g",
  },
  amazon: {
    partnerTag: "matic0f-20",
    paapiHost: "webservices.amazon.com",
    region: "us-east-1",
    marketplace: "www.amazon.com",
  },
};

const ENVIRONMENTS = {
  // Local development → Matic test store.
  test: {
    shopify: {
      storeDomain: "maticrobotstest.myshopify.com",
      installmentsStoreDomain: "xby66d-eh.myshopify.com",
      storefrontToken: "ec77be291762f4fefcc1fccf9ce21c46",
      products: {
        matic: "gid://shopify/Product/8117677260994",
        maticVariant: "gid://shopify/ProductVariant/45313327005890",
        care: "gid://shopify/Product/8466082857154",
        careVariant: "gid://shopify/ProductVariant/47369306243266",
        bagPass: "gid://shopify/Product/8452757225666",
        bagPassVariant: "gid://shopify/ProductVariant/47336344944834",
        referralBagPass: "gid://shopify/Product/8457766535362",
        referralBagPassVariant: "gid://shopify/ProductVariant/47352555536578",
        refkitVariant: "gid://shopify/ProductVariant/445313324220610",
        builderKit: "gid://shopify/Product/8509769973954",
        builderKitVariant: "gid://shopify/ProductVariant/47663185395906",
        stickerKit: "gid://shopify/Product/8681209790658",
        stickerKitVariant: "gid://shopify/ProductVariant/47943955611842",
        raceCarStickers: "gid://shopify/Product/8543151161538",
        raceCarStickersVariant: "gid://shopify/ProductVariant/47763346227394",
        petBrushRoll: "gid://shopify/Product/8117676605634",
        petBrushRollVariant: "gid://shopify/ProductVariant/45313324679362",
        dock: "gid://shopify/Product/8117676966082",
        dockVariant: "gid://shopify/ProductVariant/45313325039810",
        maticSerene: "gid://shopify/Product/15486024384706",
        maticSereneVariant: "gid://shopify/ProductVariant/67736638259394",
        bundleProductIds: [
          "gid://shopify/Product/8452757225666",
          "gid://shopify/Product/8117677097154",
          "gid://shopify/Product/8117677129922",
        ],
        accessoryProductIds: [
          "gid://shopify/Product/8117677162690",
          "gid://shopify/Product/8117677228226",
          "gid://shopify/Product/8117676966082",
          "gid://shopify/Product/8117677195458",
          "gid://shopify/Product/8117676900546",
          "gid://shopify/Product/8117676933314",
          "gid://shopify/Product/8117676867778",
          "gid://shopify/Product/8117676835010",
          "gid://shopify/Product/8543151161538",
          "gid://shopify/Product/8681209790658",
          // The same three tees as production, under the test store's ids.
          // DRAFT here too, but publishing them on test costs nothing, which
          // is the only way to exercise the size picker outside production.
          "gid://shopify/Product/8790135013570",
          "gid://shopify/Product/8790135079106",
          "gid://shopify/Product/8790135144642",
        ],
      },
    },
    web: {
      domain: "localhost",
      baseUrl:
        "https://bvwqxvfq5cjujmrb7xaim6fx3y0kqnyp.lambda-url.us-west-2.on.aws/api/web",
      cdnResizerUrl: "/_next/image",
      upgradeInsecureRequests: false,
    },
  },
  // All deploys (preview + production) → Matic production store.
  production: {
    shopify: {
      storeDomain: "xby66d-eh.myshopify.com",
      installmentsStoreDomain: "xby66d-eh.myshopify.com",
      storefrontToken: "9bdde5e971a5317d078fec1ffeef162d",
      products: {
        matic: "gid://shopify/Product/8107925471432",
        maticVariant: "gid://shopify/ProductVariant/44258632302792",
        care: "gid://shopify/Product/8308301988040",
        careVariant: "gid://shopify/ProductVariant/45041667866824",
        bagPass: "gid://shopify/Product/8308205060296",
        bagPassVariant: "gid://shopify/ProductVariant/45041219141832",
        referralBagPass: "gid://shopify/Product/8308931231944",
        referralBagPassVariant: "gid://shopify/ProductVariant/45044890534088",
        refkitVariant: "gid://shopify/ProductVariant/44753728143560",
        builderKit: "gid://shopify/Product/8237265354952",
        builderKitVariant: "gid://shopify/ProductVariant/44753615618248",
        stickerKit: "gid://shopify/Product/8323982786760",
        stickerKitVariant: "gid://shopify/ProductVariant/45121545830600",
        raceCarStickers: "gid://shopify/Product/8321445626056",
        raceCarStickersVariant: "gid://shopify/ProductVariant/45113511084232",
        petBrushRoll: "gid://shopify/Product/8333869973704",
        petBrushRollVariant: "gid://shopify/ProductVariant/45152648134856",
        dock: "gid://shopify/Product/8107925831880",
        dockVariant: "gid://shopify/ProductVariant/44258632892616",
        maticSerene: "gid://shopify/Product/8345348735176",
        maticSereneVariant: "gid://shopify/ProductVariant/45226247291080",
        bundleProductIds: [
          "gid://shopify/Product/8308205060296",
          "gid://shopify/Product/8209535140040",
          "gid://shopify/Product/8236468404424",
        ],
        accessoryProductIds: [
          "gid://shopify/Product/8209534582984",
          "gid://shopify/Product/8107925536968",
          "gid://shopify/Product/8107925831880",
          "gid://shopify/Product/8107925602504",
          "gid://shopify/Product/8107925930184",
          "gid://shopify/Product/8107925897416",
          "gid://shopify/Product/8107925995720",
          "gid://shopify/Product/8107926028488",
          "gid://shopify/Product/8237265354952",
          "gid://shopify/Product/8271398502600",
          "gid://shopify/Product/8321445626056",
          "gid://shopify/Product/8323982786760",
          // Tees, added 2026-08-26. DRAFT and unpublished at time of writing,
          // so the Storefront API returns null for them and the accessories
          // page filters them out until they are published.
          "gid://shopify/Product/8338821906632",
          "gid://shopify/Product/8338822136008",
          "gid://shopify/Product/8338822168776",
        ],
      },
    },
    web: {
      domain: "maticrobots.com",
      baseUrl: "https://api.maticrobots.com/api",
      cdnResizerUrl: "/_next/image",
      upgradeInsecureRequests: true,
    },
  },
} satisfies Record<WebEnvironment, EnvironmentConfig>;

const environment: WebEnvironment =
  process.env["NEXT_PUBLIC_WEB_ENVIRONMENT"] === "production"
    ? "production"
    : "test";

export const config = {
  ...SHARED,
  ...ENVIRONMENTS[environment],
};
